import React, { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import FilterButton from "./FilterButton";
import MinimizeButton from "./MinimizeButton";
import ShowWeeklySummaryCardsButton from "./ShowWeeklySummaryCardsButton";
import CustomizeSummaryCardsButton from "./CustomizeSummaryCardsButton";
import PrintButton from "./PrintButton";
import ShowUnpublishedShiftsButton from "./ShowUnpublishedShiftsButton";
import LoadFromTemplateButton from "./load_schedule_buttons/LoadFromTemplateButton";
import LoadFromScheduleButton from "./load_schedule_buttons/LoadFromScheduleButton";
import AutoScheduleFunction from "./load_schedule_buttons/AutoScheduleFunction";
import OptimizeBreaksDialog from "./load_schedule_buttons/OptimizeBreaksDialog";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import CopilotButton from "./load_schedule_buttons/CopilotButton";
import SaveAsTemplateButton from "./save_schedule_buttons/SaveAsTemplateButton";
import ScheduleAuditButton from "./save_schedule_buttons/ScheduleAuditButton";
import RefreshActualsButton from "./RefreshActualsButton";
import DeleteShiftsButton from "./save_schedule_buttons/DeleteShiftsButton";
import PublishScheduleButton from "./PublishScheduleButton";
import ActualIssuesPill from "./ActualIssuesPill";
import ScheduleIssuesPill from "./ScheduleIssuesPill";

const segmentOptions = [
  { label: "Department", value: "department" },
  { label: "Role", value: "role" },
];

const sortOptions = [
  { label: "Hours", value: "hours" },
  { label: "Availability", value: "availability" },
  { label: "Role", value: "role" },
  { label: "First Name", value: "first" },
  { label: "Last Name", value: "last" },
];

const toolbarButtonSx = {
  textTransform: "none",
  borderRadius: "8px",
  border: "1px solid transparent",
  color: "#1e293b",
  backgroundColor: "transparent",
  px: 1.75,
  py: 1,
  fontSize: 14,
  fontWeight: 500,
  minWidth: 0,
  lineHeight: 1.4,
  "& .MuiButton-startIcon": {
    marginRight: "8px",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "4px",
  },
  "&:hover": {
    backgroundColor: "#e2e8f0",
    borderColor: "transparent",
  },
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    borderColor: "transparent",
    color: "#94a3b8",
    "& .MuiSvgIcon-root": {
      color: "#94a3b8",
    },
  },
};

const toolbarIconSx = { fontSize: 18, color: "#1e293b" };
const toolbarChevronSx = { fontSize: 20, color: "#64748b" };

const menuPaperProps = {
  elevation: 0,
  sx: {
    mt: 1,
    minWidth: 240,
    borderRadius: 2,
    border: "1px solid #e2e8f0",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
    maxHeight: "calc(100vh - 96px)",
    overflowY: "auto",
    "& .MuiList-root": {
      py: 0.5,
    },
    // One row style for every item across the View / Tools / Auto-Schedule
    // menus, overriding the per-button defaults.
    "& .MuiMenuItem-root": {
      paddingTop: "6px !important",
      paddingBottom: "6px !important",
      minHeight: "36px !important",
    },
    "& .MuiListItemText-primary": {
      fontSize: "13px !important",
      lineHeight: 1.4,
    },
    "& .MuiListItemIcon-root": {
      minWidth: 32,
    },
    "& .MuiListItemIcon-root .MuiSvgIcon-root": {
      fontSize: 17,
    },
  },
};

function SectionLabel({ children, onClear }) {
  return (
    <div className="flex items-center justify-between px-4 pt-2 pb-0.5">
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#94a3b8",
        }}
      >
        {children}
      </span>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="text-[11px] font-medium text-slate-500 hover:text-violet-700 rounded px-1 -mr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
        >
          Clear
        </button>
      )}
    </div>
  );
}

function AutoScheduleSectionHeader({ onOpenSettings, disabled }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-2">
      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">
        Autoscheduling
      </span>
      <button
        type="button"
        aria-label="Open autoschedule settings"
        onClick={onOpenSettings}
        disabled={disabled}
        className="flex min-h-[36px] items-center gap-1.5 rounded-md px-2 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-slate-300"
      >
        <SettingsOutlinedIcon sx={{ fontSize: 15 }} />
        Settings
      </button>
    </div>
  );
}

function RadioMenuItem({ label, active, onClick }) {
  return (
    <MenuItem onClick={onClick} sx={{ pl: 2.5 }}>
      <ListItemText
        primaryTypographyProps={{
          fontSize: 13,
          color: active ? "#6d28d9" : "#334155",
          fontWeight: active ? 600 : 400,
        }}
      >
        {label}
      </ListItemText>
      {active && <CheckIcon sx={{ color: "#6d28d9", fontSize: 18 }} />}
    </MenuItem>
  );
}

function ActionBarDisplay2(props) {
  const [viewAnchor, setViewAnchor] = useState(null);
  const [toolsAnchor, setToolsAnchor] = useState(null);
  const [autoAnchor, setAutoAnchor] = useState(null);
  const [breaksMode, setBreaksMode] = useState(null);

  const autoScheduleRef = useRef(null);

  const disabledEverything = props.showWarnings;

  // Actual mode is a read of what happened, so the controls that rewrite the
  // schedule are out of reach there. View and Filter stay enabled: they change
  // what you are looking at, not what is scheduled. Disabled rather than
  // hidden, so the toolbar does not reflow every time the mode is toggled and
  // nobody has to hunt for a button that was there a second ago.
  const schedulingDisabled = disabledEverything || !!props.showActualHours;
  const schedulingDisabledReason = props.showActualHours
    ? "Switch to Planned to edit the schedule"
    : "";

  const hasActiveView =
    (props.segmentMode && props.segmentMode !== "default") ||
    !!props.sorted ||
    props.reorderMode ||
    props.minimizeMode ||
    props.showWeeklySummaryCards;

  const sortOptionsFiltered = props.weeklyView
    ? sortOptions
    : [{ label: "Start Time", value: "shift_start" }, ...sortOptions];

  const closeView = () => setViewAnchor(null);
  const closeTools = () => setToolsAnchor(null);
  const closeAuto = () => setAutoAnchor(null);

  const autoScheduleIcon = () => (
    <AutoFixHighOutlinedIcon color={props.loadedFromAuto ? "success" : "default"} />
  );
  const autoScheduleText = () => (
    <div id="modal-modal-description">
      <h3>
        Auto Schedule will automatically build you an optimized schedule based on your demand forecast and team member availability.
      </h3>
      <p className="mt-3">Lock any team members or days that want to keep the same.</p>
      <p className="mt-3">This process will take a few minutes.</p>
    </div>
  );

  const fullWeekPastAutoscheduleEnabled =
    Boolean(props.fullWeekPastAutoscheduleEnabled);
  const autoActionsDisabled =
    props.week_ended || !props.autosched_available || !props.laborModelEnabled;
  const fullAutoScheduleDisabled =
    (props.week_ended && !fullWeekPastAutoscheduleEnabled) ||
    !props.autosched_available || !props.laborModelEnabled;
  const fullAutoScheduleMenuDisabled =
    fullAutoScheduleDisabled || disabledEverything;
  const autoActionsTooltip =
    props.week_ended
      ? "Autoschedule not available for past weeks"
      : !props.autosched_available
      ? "Autoschedule not available"
      : "";
  const fullAutoScheduleTooltip =
    props.week_ended && !fullWeekPastAutoscheduleEnabled
      ? "Autoschedule not available for past weeks"
      : !props.autosched_available
      ? "Autoschedule not available"
      : "";

  const triggerAutoRun = (runType) => {
    closeAuto();
    const runDisabled =
      runType === "autoschedule"
        ? fullAutoScheduleMenuDisabled
        : autoActionsDisabled || disabledEverything;
    if (runDisabled) return;
    autoScheduleRef.current?.runDirect(runType);
  };

  const openAutoScheduleSettings = () => {
    closeAuto();
    if (disabledEverything) return;
    autoScheduleRef.current?.openSettings();
  };

  return (
    <div className="w-full p-2.5 rounded-xl bg-slate-50">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* View */}
          <Button
            data-help-id="schedule-view-menu"
            disabled={disabledEverything}
            onClick={(e) => setViewAnchor(e.currentTarget)}
            disableRipple
            startIcon={
              <ViewListOutlinedIcon
                sx={{
                  ...toolbarIconSx,
                  color:
                    !disabledEverything && hasActiveView
                      ? "#6d28d9"
                      : toolbarIconSx.color,
                }}
              />
            }
            endIcon={<KeyboardArrowDownIcon sx={toolbarChevronSx} />}
            sx={{
              ...toolbarButtonSx,
              color:
                !disabledEverything && hasActiveView ? "#6d28d9" : "#1e293b",
            }}
          >
            View
          </Button>
          <Menu
            anchorEl={viewAnchor}
            open={Boolean(viewAnchor)}
            onClose={closeView}
            keepMounted
            PaperProps={menuPaperProps}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {!props.monthlyView && (
              <SectionLabel
                onClear={
                  props.segmentMode && props.segmentMode !== "default"
                    ? () => {
                        props.setSegmentMode("default");
                        closeView();
                      }
                    : undefined
                }
              >
                Group by
              </SectionLabel>
            )}
            {!props.monthlyView &&
              segmentOptions.map((opt) => (
                <RadioMenuItem
                  key={opt.value}
                  label={opt.label}
                  active={props.segmentMode === opt.value}
                  onClick={() => {
                    props.setSegmentMode(
                      props.segmentMode === opt.value ? "default" : opt.value
                    );
                    closeView();
                  }}
                />
              ))}
            {!props.monthlyView && <Divider sx={{ my: 0.5 }} />}

            {!props.monthlyView && (
              <SectionLabel
                onClear={
                  props.sorted
                    ? () => {
                        props.setSorted("");
                        props.sortTeam("");
                        closeView();
                      }
                    : undefined
                }
              >
                Sort by
              </SectionLabel>
            )}
            {!props.monthlyView &&
              sortOptionsFiltered.map((opt) => (
                <RadioMenuItem
                  key={opt.value}
                  label={opt.label}
                  active={props.sorted === opt.value}
                  onClick={() => {
                    const nextValue = props.sorted === opt.value ? "" : opt.value;
                    props.setSorted(nextValue);
                    props.sortTeam(nextValue);
                    closeView();
                  }}
                />
              ))}
            {!props.monthlyView && <Divider sx={{ my: 0.5 }} />}

            {props.weeklyView && (
              <>
                <SectionLabel>Metric card row</SectionLabel>
                {[
                  { value: "metric", label: props.laborModelEnabled ? "Labor bars" : "Hours" },
                  { value: "chart", label: "Intraday charts" },
                ].map((opt) => (
                  <RadioMenuItem
                    key={opt.value}
                    label={opt.label}
                    active={(props.bottomCardType || "metric") === opt.value}
                    onClick={() => {
                      props.setBottomCardType?.(opt.value);
                      closeView();
                    }}
                  />
                ))}
                <Divider sx={{ my: 0.5 }} />
              </>
            )}

            {props.weeklyView && (
              <MenuItem
                onClick={() => {
                  props.toggleReorderMode();
                  closeView();
                }}
                sx={{ py: 1 }}
              >
                <ListItemIcon
                  sx={{ color: props.reorderMode ? "#6d28d9" : undefined }}
                >
                  <DragIndicatorIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Reorder employees"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: props.reorderMode ? 600 : 400,
                    color: props.reorderMode ? "#6d28d9" : "#334155",
                  }}
                />
                {props.reorderMode && (
                  <CheckIcon sx={{ color: "#6d28d9", fontSize: 18 }} />
                )}
              </MenuItem>
            )}

            {props.weeklyView && (
              <MinimizeButton
                renderAsMenuItem
                minimizeMode={props.minimizeMode}
                toggleMinimizeMode={props.toggleMinimizeMode}
                onMenuItemClick={closeView}
              />
            )}

            {(props.weeklyView || props.monthlyView || props.dailyView) && (
              <PrintButton
                renderAsMenuItem
                dailyView={props.dailyView}
                dowSelected={props.dowSelected}
                scheduleLoading={props.scheduleLoading}
                addDays={props.addDays}
                week_start_date={props.week_start_date}
                store_team_member_weekly_info={props.store_team_member_weekly_info}
                store_team_member_weekly_info_view={
                  props.store_team_member_weekly_info_view
                }
                get_t_str={props.get_t_str}
                weeklyView={props.weeklyView}
                monthlyView={props.monthlyView}
                notes={props.notes}
                actual_schedule={props.actual_schedule}
                role_names={props.role_names}
                filter_df_to_day={props.filter_df_to_day}
                printHoursShownEnabled={props.printHoursShownEnabled}
                storeNames={props.storeNames}
                store_id={props.store_id}
                currentMonth={props.currentMonth}
                currentYear={props.currentYear}
                businessWeekStartDayIndex={props.businessWeekStartDayIndex}
                selectedRolesForFiltering={props.selectedRolesForFiltering}
                onMenuItemClick={closeView}
              />
            )}

            {props.weeklyView && <Divider sx={{ my: 0.5 }} />}

            {props.weeklyView && (
              <ShowWeeklySummaryCardsButton
                showWeeklySummaryCards={props.showWeeklySummaryCards}
                toggleShowWeeklySummaryCards={props.toggleShowWeeklySummaryCards}
                onMenuItemClick={closeView}
              />
            )}

            {props.weeklyView && props.openCustomizeSummaryCards && (
              <CustomizeSummaryCardsButton
                openCustomizeSummaryCards={props.openCustomizeSummaryCards}
                onMenuItemClick={closeView}
              />
            )}
          </Menu>

          {/* Filter */}
          <FilterButton
            toolbarMode
            filterTeam={props.filterTeam}
            filtered={props.filtered}
            weeklyView={props.weeklyView}
            monthlyView={props.monthlyView}
            role_names={props.role_names}
            selectedRolesForFiltering={props.selectedRolesForFiltering}
            setSelectedRolesForFiltering={props.setSelectedRolesForFiltering}
            showWarnings={props.showWarnings}
          />

          {/* Tools */}
          {!props.monthlyView && (
            <>
              <Tooltip title={schedulingDisabledReason}>
              <span style={{ display: "inline-flex" }}>
              <Button
                data-help-id="schedule-tools-menu"
                disabled={disabledEverything}
                onClick={(e) => setToolsAnchor(e.currentTarget)}
                disableRipple
                startIcon={
                  <BuildOutlinedIcon
                    sx={{
                      ...toolbarIconSx,
                      color:
                        !disabledEverything && props.showUnpublishedShifts
                          ? "#6d28d9"
                          : toolbarIconSx.color,
                    }}
                  />
                }
                endIcon={<KeyboardArrowDownIcon sx={toolbarChevronSx} />}
                sx={{
                  ...toolbarButtonSx,
                  color:
                    !disabledEverything && props.showUnpublishedShifts
                      ? "#6d28d9"
                      : "#1e293b",
                }}
              >
                Tools
              </Button>
              </span>
              </Tooltip>
              <Menu
                anchorEl={toolsAnchor}
                open={Boolean(toolsAnchor)}
                onClose={closeTools}
                keepMounted
                PaperProps={menuPaperProps}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {/* Everything under Templates writes to the schedule, so it
                    goes while actuals are shown. Schedule below it is reads and
                    the actuals refresh, which is the reason the menu itself
                    stays reachable. */}
                {!props.showActualHours && (
                  <>
                <SectionLabel>Templates</SectionLabel>
                {props.weeklyView && (
                  <LoadFromTemplateButton
                    renderAsMenuItem
                    week_already_started={props.week_already_started}
                    loadFromTemplate={props.loadFromTemplate}
                    deleteScheduleTemplate={props.deleteScheduleTemplate}
                    template_names={props.template_names}
                    loadFromTemplateFinished={props.loadFromTemplateFinished}
                    setLoadFromTemplateFinished={props.setLoadFromTemplateFinished}
                    loadedFromTemplate={props.loadedFromTemplate}
                    onMenuItemClick={closeTools}
                  />
                )}
                {!props.weeklyView &&
                  !props.monthlyView &&
                  props.dailyTemplateProps?.saveDailyScheduleAsTemplate && (
                    <LoadFromTemplateButton
                      renderAsMenuItem
                      mode="daily"
                      week_already_started={props.dailyTemplateProps.selectedDayInPast}
                      loadFromTemplate={(name, keep) =>
                        props.dailyTemplateProps.loadDailyFromTemplate(
                          name,
                          keep,
                          props.dailyTemplateProps.selectedDateStr
                        )
                      }
                      deleteScheduleTemplate={
                        props.dailyTemplateProps.deleteDailyScheduleTemplate
                      }
                      template_names={props.dailyTemplateProps.dailyTemplateNames}
                      loadFromTemplateFinished={
                        props.dailyTemplateProps.loadFromTemplateFinished
                      }
                      setLoadFromTemplateFinished={
                        props.dailyTemplateProps.setLoadFromTemplateFinished
                      }
                      onMenuItemClick={closeTools}
                    />
                  )}
                <LoadFromScheduleButton
                  renderAsMenuItem
                  week_already_started={props.week_already_started}
                  loadFrom7Shift={props.loadFrom7Shift}
                  week_start_date={props.week_start_date}
                  load7ShiftsScheduleFinished={props.load7ShiftsScheduleFinished}
                  setLoad7ShiftsScheduleFinished={props.setLoad7ShiftsScheduleFinished}
                  loadedFrom7Shifts={props.loadedFrom7Shifts}
                  has7ShiftsIntegration={props.has7ShiftsIntegration}
                  onMenuItemClick={closeTools}
                />
                {props.weeklyView && (
                  <SaveAsTemplateButton
                    renderAsMenuItem
                    saveScheduleAsTemplate={props.saveScheduleAsTemplate}
                    template_names={props.template_names}
                    formatDate={props.formatDate}
                    scheduled_hours={props.scheduled_hours}
                    monthlyView={props.monthlyView}
                    onMenuItemClick={closeTools}
                  />
                )}
                {!props.weeklyView &&
                  !props.monthlyView &&
                  props.dailyTemplateProps?.saveDailyScheduleAsTemplate && (
                    <SaveAsTemplateButton
                      renderAsMenuItem
                      mode="daily"
                      saveScheduleAsTemplate={(name) =>
                        props.dailyTemplateProps.saveDailyScheduleAsTemplate(
                          name,
                          props.dailyTemplateProps.selectedDateStr
                        )
                      }
                      template_names={props.dailyTemplateProps.dailyTemplateNames}
                      formatDate={props.dailyTemplateProps.formatDate}
                      scheduled_hours={props.dailyTemplateProps.selectedDayShifts}
                      onMenuItemClick={closeTools}
                    />
                  )}
                <Divider sx={{ my: 0.5 }} />
                  </>
                )}
                <SectionLabel>Schedule</SectionLabel>
                <ShowUnpublishedShiftsButton
                  renderAsMenuItem
                  showUnpublishedShifts={props.showUnpublishedShifts}
                  setShowUnpublishedShifts={props.setShowUnpublishedShifts}
                  monthlyView={props.monthlyView}
                  onMenuItemClick={closeTools}
                />
                {/* The audit modal can revert the published schedule, so it
                    is not the read-only history it looks like. */}
                {!props.showActualHours && (
                <ScheduleAuditButton
                  renderAsMenuItem
                  saveScheduleAsTemplate={props.saveScheduleAsTemplate}
                  template_names={props.template_names}
                  formatDate={props.formatDate}
                  scheduled_hours={props.scheduled_hours}
                  publishedScheduleActionLog={props.publishedScheduleActionLog}
                  start_date={props.start_date}
                  end_date={props.end_date}
                  monthlyView={props.monthlyView}
                  store_id={props.store_id}
                  onScheduleReverted={props.onScheduleReverted}
                  onMenuItemClick={closeTools}
                />
                )}
                <RefreshActualsButton
                  pos_provider={props.pos_provider}
                  store_id={props.store_id}
                  start_date={props.start_date}
                  end_date={props.end_date}
                  formatDate={props.formatDate}
                  refreshActualsForDate={props.refreshActualsForDate}
                  monthlyView={props.monthlyView}
                  onMenuItemClick={closeTools}
                />
                {!props.showActualHours && (
                  <>
                <Divider sx={{ my: 0.5 }} />
                <DeleteShiftsButton
                  renderAsMenuItem
                  deleteAllShifts={props.deleteAllShifts}
                  week_already_started={props.week_already_started}
                  monthlyView={props.monthlyView}
                  onMenuItemClick={closeTools}
                />
                  </>
                )}
              </Menu>
            </>
          )}

          {/* Auto-Schedule */}
          {!props.monthlyView && (props.laborModelEnabled || props.canOptimizeBreaks) && (
            <>
              <Tooltip title={schedulingDisabledReason}>
              <span style={{ display: "inline-flex" }}>
              <Button
                onClick={(e) => setAutoAnchor(e.currentTarget)}
                disabled={schedulingDisabled}
                disableRipple
                startIcon={
                  <AutoFixHighOutlinedIcon
                    sx={{
                      ...toolbarIconSx,
                      color:
                        !disabledEverything && props.isCopilotOn
                          ? "#6d28d9"
                          : props.loadedFromAuto
                          ? "#059669"
                          : toolbarIconSx.color,
                    }}
                  />
                }
                endIcon={<KeyboardArrowDownIcon sx={toolbarChevronSx} />}
                sx={{
                  ...toolbarButtonSx,
                  color:
                    !disabledEverything && props.isCopilotOn
                      ? "#6d28d9"
                      : "#1e293b",
                }}
              >
                Auto-Schedule
              </Button>
              </span>
              </Tooltip>
              <Menu
                anchorEl={autoAnchor}
                open={Boolean(autoAnchor)}
                onClose={closeAuto}
                keepMounted
                PaperProps={menuPaperProps}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <AutoScheduleSectionHeader
                  onOpenSettings={openAutoScheduleSettings}
                  disabled={disabledEverything}
                />
                <Tooltip
                  title={fullAutoScheduleDisabled ? fullAutoScheduleTooltip : ""}
                  placement="right"
                >
                  <span>
                    <MenuItem
                      onClick={() => triggerAutoRun("autoschedule")}
                      disabled={fullAutoScheduleDisabled}
                      sx={{ py: 1.25 }}
                    >
                      <ListItemIcon>
                        <AutoFixHighOutlinedIcon
                          fontSize="small"
                          sx={{
                            color: fullAutoScheduleDisabled
                              ? undefined
                              : "#6d28d9",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Auto-Schedule"
                        secondary="Build a full schedule from scratch"
                        primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: 12, color: "#64748b" }}
                      />
                    </MenuItem>
                  </span>
                </Tooltip>
                <Tooltip
                  title={autoActionsDisabled ? autoActionsTooltip : ""}
                  placement="right"
                >
                  <span>
                    <MenuItem
                      onClick={() => triggerAutoRun("autotune")}
                      disabled={autoActionsDisabled}
                      sx={{ py: 1.25 }}
                    >
                      <ListItemIcon>
                        <TuneOutlinedIcon
                          fontSize="small"
                          sx={{ color: autoActionsDisabled ? undefined : "#6d28d9" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Auto-Tune"
                        secondary="Keep shifts, adjust start/stop times"
                        primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: 12, color: "#64748b" }}
                      />
                    </MenuItem>
                  </span>
                </Tooltip>
                <Tooltip
                  title={autoActionsDisabled ? autoActionsTooltip : ""}
                  placement="right"
                >
                  <span>
                    <MenuItem
                      onClick={() => triggerAutoRun("autofill")}
                      disabled={autoActionsDisabled}
                      sx={{ py: 1.25 }}
                    >
                      <ListItemIcon>
                        <PersonAddAlt1OutlinedIcon
                          fontSize="small"
                          sx={{ color: autoActionsDisabled ? undefined : "#6d28d9" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Auto-Fill"
                        secondary="Fill in open or missing shifts"
                        primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: 12, color: "#64748b" }}
                      />
                    </MenuItem>
                  </span>
                </Tooltip>
                {props.canOptimizeBreaks && [
                  { mode: "optimize", label: "Optimize breaks", description: "Add and stagger breaks on existing shifts" },
                  { mode: "add_missing", label: "Add missing breaks only", description: "Keep existing breaks in place" },
                ].map((action) => <MenuItem key={action.mode}
                  onClick={() => { closeAuto(); setBreaksMode(action.mode); }}
                  disabled={schedulingDisabled || props.scheduleLoading}
                  sx={{ py: 1.25 }}
                >
                  <ListItemIcon><FreeBreakfastOutlinedIcon fontSize="small" sx={{ color: "#6d28d9" }} /></ListItemIcon>
                  <ListItemText primary={action.label} secondary={action.description}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: 12, color: "#64748b" }} />
                </MenuItem>)}
                {props.copilotEnabled && [
                  <Divider key="divider" sx={{ my: 0.5 }} />,
                  <Tooltip
                    key="copilot-tooltip"
                    title={autoActionsDisabled ? autoActionsTooltip : ""}
                    placement="right"
                  >
                    <span>
                      <MenuItem
                        onClick={() => {
                          props.setIsCopilotOn((prev) => !prev);
                          closeAuto();
                        }}
                        disabled={autoActionsDisabled}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon
                          sx={{ color: autoActionsDisabled ? undefined : "#6d28d9" }}
                        >
                          <AutoAwesomeOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: 14,
                            color:
                              !autoActionsDisabled && props.isCopilotOn
                                ? "#6d28d9"
                                : "#334155",
                            fontWeight: props.isCopilotOn ? 600 : 400,
                          }}
                        >
                          Copilot suggestions
                        </ListItemText>
                        {!autoActionsDisabled && props.isCopilotOn && (
                          <CheckIcon sx={{ color: "#6d28d9", fontSize: 18 }} />
                        )}
                      </MenuItem>
                    </span>
                  </Tooltip>,
                ]}
              </Menu>

              {breaksMode && props.canOptimizeBreaks && !schedulingDisabled && <OptimizeBreaksDialog
                key={`${props.store_id}-${breaksMode}-${JSON.stringify(props.breakOptimizationOptions)}`}
                storeId={props.store_id} options={props.breakOptimizationOptions} mode={breaksMode}
                comparisonProps={{
                  store_team_member_weekly_info: props.store_team_member_weekly_info,
                  grade_weights: props.grade_weights, role_names: props.role_names,
                  storeNames: props.storeNames, store_id: props.store_id, get_t_str: props.get_t_str,
                }}
                onClose={() => setBreaksMode(null)} onApplied={props.onBreaksApplied}
              />}

              {/* Hidden modal host — triggered imperatively via ref */}
              <AutoScheduleFunction
                ref={autoScheduleRef}
                hideTrigger
                employeeSchedulingMembers={props.employeeSchedulingMembers}
                canManageEmployeePreferences={props.canManageEmployeeSchedulingPreferences}
                updatePersonInfo={props.updatePersonInfo}
                autoScheduleText={autoScheduleText}
                autoScheduleTitle="Autoscheduling Options"
                autoScheduleButtonText="Auto Schedule"
                autoScheduleIcon={autoScheduleIcon}
                runType={"autoschedule"}
                autosched_available={props.autosched_available}
                week_ended={props.week_ended}
                fullWeekPastAutoscheduleEnabled={
                  fullWeekPastAutoscheduleEnabled
                }
                comparisonMetricVisibility={props.comparisonMetricVisibility}
                autoSchedule={props.autoSchedule}
                acceptNewSchedule={props.acceptNewSchedule}
                schedule_differences={props.schedule_differences}
                autoScheduleError={props.autoScheduleError}
                setAutoScheduleError={props.setAutoScheduleError}
                autoScheduleFinished={props.autoScheduleFinished}
                setAutoScheduleFinished={props.setAutoScheduleFinished}
                acceptAutoScheduleFinished={props.acceptAutoScheduleFinished}
                setAcceptAutoScheduleFinished={props.setAcceptAutoScheduleFinished}
                loadedFromAuto={props.loadedFromAuto}
                lockAllShifts={props.lockAllShifts}
                setLockAllShifts={props.setLockAllShifts}
                store_team_member_weekly_info={props.store_team_member_weekly_info}
                get_t_str={props.get_t_str}
                actual_schedule={props.actual_schedule}
                actual_schedule_labor={props.actual_schedule_labor}
                forecasted_labor={props.forecasted_labor}
                grade_weights={props.grade_weights}
                role_types={props.role_types}
                role_names={props.role_names}
                week_start_date={props.week_start_date}
                storeNames={props.storeNames}
                store_id={props.store_id}
              />

              {/* Copilot suggestions UI remains visible when enabled */}
              {props.copilotEnabled && props.isCopilotOn && (
                <CopilotButton
                  autosched_available={props.autosched_available}
                  week_ended={props.week_ended}
                  isCopilotOn={props.isCopilotOn}
                  setIsCopilotOn={props.setIsCopilotOn}
                  copilotShifts={props.copilotShifts}
                  copilotDeleteShifts={props.copilotDeleteShifts}
                  addMultipleShifts={props.addMultipleShifts}
                  isAddingAllCopilotShiftsLoading={
                    props.isAddingAllCopilotShiftsLoading
                  }
                  setIsAddingAllCopilotShiftsLoading={
                    props.setIsAddingAllCopilotShiftsLoading
                  }
                  isCopilotLoading={props.isCopilotLoading}
                  copilotEnabled={props.copilotEnabled}
                  week_start_date={props.week_start_date}
                  store_id={props.store_id}
                />
              )}
            </>
          )}
        </div>

        {/* Issues + Publish */}
        <div className="flex items-center gap-2.5">
          {props.weeklyView && props.showActualHours && props.laborLawEnabled && (
            <ActualIssuesPill
              key={`${props.store_id}-${props.actualAuditStartDate}`}
              audit={props.actualComplianceAudit}
              startDate={props.actualAuditStartDate}
              endDate={props.actualAuditEndDate}
            />
          )}
          {props.weeklyView && !props.showActualHours && (
            <ScheduleIssuesPill
              countOfWarnings={props.countOfWarnings}
              laborLawEnabled={props.laborLawEnabled}
              laborLawViolations={props.laborLawViolations}
              showWarnings={props.showWarnings}
              toggleShowWarnings={() =>
                props.setShowWarnings((prevState) => !prevState)
              }
              start_date={props.start_date}
              end_date={props.end_date}
              blockPublishOnLaborViolationsEnabled={
                props.blockPublishOnLaborViolationsEnabled
              }
              roleType={props.roleType}
              roleTypeOptions={props.roleTypeOptions}
              windowWidth={props.windowWidth}
            />
          )}
          <PublishScheduleButton
            // Publishing does not depend on how the board is sorted, so the
            // issues sort leaves it alone; only Actual mode locks it.
            disabled={!!props.showActualHours}
            disabledReason={schedulingDisabledReason}
            publishShifts={props.publishShifts}
            roleType={props.roleType}
            roleTypeOptions={props.roleTypeOptions}
            publishShiftsFinished={props.publishShiftsFinished}
            setPublishShiftsFinished={props.setPublishShiftsFinished}
            pushedChanges={props.pushedChanges}
            has7ShiftsIntegration={props.has7ShiftsIntegration}
            countOfWarnings={props.countOfWarnings}
            countOfUnpublished={props.countOfUnpublished}
            countOfPublished={props.countOfPublished}
            laborLawEnabled={props.laborLawEnabled}
            laborLawViolations={props.laborLawViolations}
            monthlyView={props.monthlyView}
            monthName={props.monthName}
            publishScheduleReviewRequiredEnabled={
              props.publishScheduleReviewRequiredEnabled
            }
            blockPublishOnLaborViolationsEnabled={
              props.blockPublishOnLaborViolationsEnabled
            }
            canPublishSchedule={props.canPublishSchedule}
            windowWidth={props.windowWidth}
          />
        </div>
      </div>
    </div>
  );
}

export default ActionBarDisplay2;
